import { useContext, useRef, useState } from "react";
import UiContext from "@store/ui-context";
import useAPIRequest from "@custom_hooks/simple/useAPIRequest";
import {
  buildSectionBody,
  generateNewPermintaanMinyak,
  validateSection,
} from "../utils/permintaan-minyak-helpers";

export const useAPIEditPermintaanMinyak = (props) => {
  // onChanged: optional, called when the document was saved / loaded again after a signature
  const { onChanged } = props ?? {};

  const uiContext = useContext(UiContext);
  const { requestGetOne, requestPost, loading } = useAPIRequest();

  const [data, setData] = useState({});
  const [error, setError] = useState({});

  // the document as the backend holds it: to know if a section has unsaved changes
  const savedDataRef = useRef({});

  //  API call section

  const fetchData = async ({ id, extraData }) => {
    return requestGetOne({
      fullUrl: `api/transaksi/permintaanminyak/one/${id}`,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data: _data, message }) => {
        savedDataRef.current = _data;
        if (extraData) {
          setData({ ...extraData, ..._data });
        } else {
          setData(_data);
        }
      },
    });
  };

  const isSectionChanged = (section) =>
    JSON.stringify(buildSectionBody(section, data)) !==
    JSON.stringify(buildSectionBody(section, savedDataRef.current));

  // load the document again (e.g. after a signature) and tell the list
  // id: for a document that was created a moment ago, when data.id is not known here yet
  const reload = async (id) => {
    const reloadId = typeof id === "number" ? id : data.id;
    if (!reloadId) return null;

    const newData = await fetchData({ id: reloadId });
    if (newData && onChanged) onChanged(newData);
    return newData;
  };

  // save the fields of one section; returns the saved document, or false
  // skipIfUnchanged: an existing document whose section was not edited is not saved again
  const saveSection = async (
    section,
    { strict = false, skipIfUnchanged = false } = {}
  ) => {
    const newError = validateSection(section, data, strict);
    setError(newError);

    if (Object.keys(newError).length > 0) {
      uiContext.showErrorMsg("Error", "Data belum lengkap");
      return false;
    }

    if (skipIfUnchanged && data.id > 0 && !isSectionChanged(section)) {
      return data;
    }

    // a new document is created with "save" (the backend requires nomor, department, jenis minyak
    // and jumlah diminta there); a document that exists is changed with "update", which takes
    // only the id and the fields of the section
    const isNew = !(data.id > 0);

    const result = await requestPost({
      fullUrl: `/api/transaksi/permintaanminyak/${isNew ? "save" : "update"}`,
      body: isNew
        ? {
            ...buildSectionBody("pengajuan", data),
            ...buildSectionBody(section, data),
          }
        : buildSectionBody(section, data),
      isForm: false,
      onError: ({ message, data: errorData }) => {
        uiContext.showErrorMsg("Error", message);
        if (errorData) setError(errorData);
      },
      onSuccess: ({ message }) => {
        uiContext.showSuccessMsg("Success", message);
      },
    });

    if (!result) return false;

    // read the document again from the normal "one" endpoint: it has the relations and the
    // status as the backend holds them now
    const fresh = await fetchData({ id: result.id ?? data.id });
    if (fresh) {
      if (onChanged) onChanged(fresh);
      return fresh;
    }

    // could not read it again: keep what is on screen plus the response of the save
    const merged = { ...data, ...result };
    savedDataRef.current = merged;
    setData(merged);
    if (onChanged) onChanged(merged);
    return merged;
  };

  // other function section

  const fetchDataOrCreateNew = ({ id, extraData }) => {
    setError({});
    if (id && id > 0) {
      fetchData({ id, extraData });
    } else {
      setData(
        extraData
          ? { ...generateNewPermintaanMinyak(), ...extraData }
          : generateNewPermintaanMinyak()
      );
    }
  };

  return {
    loading,
    data,
    setData,
    error,
    setError,
    fetchDataOrCreateNew,
    reload,
    saveSection,
  };
};
